/**
 * Bidirectional sync between the Details form and the Calendar panel.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { FormBuilderHandle, CalendarHandle } from '@kula-ai/components';
import type { ScheduleSharedState } from '../state/useScheduleSharedState';
import moment from 'moment-timezone';
import {
  formatTime12,
  buildIsoFromDateAndTime,
  addMinutesToIso,
  diffMinutesIso,
  snapToNearest,
  findClosestTimeOption,
  parse12hrTime,
} from '../steps/details/fields/dateTime';

/** Scroll the booked slot into view by scrolling the calendar's fc-scroller. */
function scrollBookedSlotIntoView() {
  setTimeout(() => {
    const bookedEl = document.querySelector('.im-calendar .booked-event');
    if (bookedEl) {
      bookedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 200);
}

interface UseFormCalendarSyncOptions {
  formRef: React.RefObject<FormBuilderHandle | null>;
  sharedState: ScheduleSharedState;
  updateSharedState: (_partial: Partial<ScheduleSharedState>) => void;
  calendarRef?: React.RefObject<CalendarHandle | null>;
}

export function useFormCalendarSync({
  formRef,
  sharedState,
  updateSharedState,
  calendarRef,
}: UseFormCalendarSyncOptions) {
  const skipSyncRef = useRef(false);
  const timezone = sharedState.timezone;

  // ---- Form → Shared State ----
  const handleFieldChange = useCallback(
    (name: string, value: unknown, _allValues: Record<string, unknown>) => {
      if (skipSyncRef.current) return;

      const form = formRef.current;
      const duration = sharedState.bookedSlot?.duration ?? 30;

      switch (name) {
        case 'interviewName':
          updateSharedState({ interviewName: value as string });
          break;

        case 'startDate': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          let startTimeStr = (vals?.startTime as string) || '';
          if (!startTimeStr && sharedState.bookedSlot?.start) {
            startTimeStr = formatTime12(sharedState.bookedSlot.start, timezone);
          }
          if (!value || !startTimeStr) break;

          // Reject past dates
          const selectedDate = moment(value as Date)
            .tz(timezone)
            .startOf('day');
          const today = moment().tz(timezone).startOf('day');
          if (selectedDate.isBefore(today)) {
            break;
          }

          // If switching to today and current startTime is in the past, reject
          if (selectedDate.isSame(today, 'day')) {
            const parsed = parse12hrTime(startTimeStr);
            if (parsed) {
              const nowMins =
                moment().tz(timezone).hours() * 60 +
                moment().tz(timezone).minutes();
              const selectedMins = parsed.hours * 60 + parsed.minutes;
              if (selectedMins <= nowMins) {
                // Revert date to previous value
                if (sharedState.bookedSlot?.start) {
                  skipSyncRef.current = true;
                  form?.setValue(
                    'startDate',
                    new Date(sharedState.bookedSlot.start)
                  );
                  skipSyncRef.current = false;
                }
                break;
              }
            }
          }

          const newStart = buildIsoFromDateAndTime(
            value as Date,
            startTimeStr,
            timezone
          );
          if (!newStart) break;
          const newEnd = addMinutesToIso(newStart, duration);
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            bookedSlot: { start: newStart, end: newEnd, duration },
          });
          skipSyncRef.current = true;
          form?.setValue('endTime', findClosestTimeOption(endTimeStr));
          form?.setValue('dateTime', `${newStart}|${newEnd}`);
          skipSyncRef.current = false;
          calendarRef?.current?.goToDate(value as Date);
          scrollBookedSlotIntoView();
          break;
        }

        case 'startTime': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          // startDate may be null if the date field was conditionally unmounted
          // Fall back to deriving from dateTime string
          let startDate = vals?.startDate as Date | null;
          if (!startDate && sharedState.bookedSlot?.start) {
            startDate = new Date(sharedState.bookedSlot.start);
          }
          if (!startDate || !value) break;

          // Reject past times if selected date is today
          const isToday = moment(startDate)
            .tz(timezone)
            .isSame(moment().tz(timezone), 'day');
          if (isToday) {
            const parsed = parse12hrTime(value as string);
            if (parsed) {
              const nowMins =
                moment().tz(timezone).hours() * 60 +
                moment().tz(timezone).minutes();
              const selectedMins = parsed.hours * 60 + parsed.minutes;
              if (selectedMins < nowMins) {
                // Revert to previous valid startTime
                const prevStartTime = formatTime12(
                  sharedState.bookedSlot?.start ?? '',
                  timezone
                );
                if (prevStartTime) {
                  skipSyncRef.current = true;
                  form?.setValue(
                    'startTime',
                    findClosestTimeOption(prevStartTime)
                  );
                  skipSyncRef.current = false;
                }
                break;
              }
            }
          }

          const newStart = buildIsoFromDateAndTime(
            startDate,
            value as string,
            timezone
          );
          if (!newStart) break;
          const newEnd = addMinutesToIso(newStart, duration);
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            bookedSlot: { start: newStart, end: newEnd, duration },
          });
          skipSyncRef.current = true;
          form?.setValue('endTime', findClosestTimeOption(endTimeStr));
          form?.setValue('dateTime', `${newStart}|${newEnd}`);
          skipSyncRef.current = false;
          scrollBookedSlotIntoView();
          break;
        }

        case 'endTime': {
          const vals = form?.getValues() as Record<string, unknown> | undefined;
          let startDate = vals?.startDate as Date | null;
          if (!startDate && sharedState.bookedSlot?.start) {
            startDate = new Date(sharedState.bookedSlot.start);
          }
          const startTimeStr = (vals?.startTime as string) || '';
          if (!startDate || !startTimeStr || !value) break;

          // Reject end time before or equal to start time
          const startParsed = parse12hrTime(startTimeStr);
          const endParsed = parse12hrTime(value as string);
          if (startParsed && endParsed) {
            const startMins = startParsed.hours * 60 + startParsed.minutes;
            const endMins = endParsed.hours * 60 + endParsed.minutes;
            if (endMins <= startMins) {
              // Revert to previous endTime
              const prevEndTime = formatTime12(
                sharedState.bookedSlot?.end ?? '',
                timezone
              );
              if (prevEndTime) {
                skipSyncRef.current = true;
                form?.setValue('endTime', findClosestTimeOption(prevEndTime));
                skipSyncRef.current = false;
              }
              break;
            }
          }

          const startIso = buildIsoFromDateAndTime(
            startDate,
            startTimeStr,
            timezone
          );
          const endIso = buildIsoFromDateAndTime(
            startDate,
            value as string,
            timezone
          );
          if (!startIso || !endIso) break;
          const newDuration = diffMinutesIso(startIso, endIso);
          if (newDuration <= 0) break;
          const snappedDuration = snapToNearest(newDuration);
          updateSharedState({
            bookedSlot: {
              start: startIso,
              end: endIso,
              duration: snappedDuration,
            },
          });
          skipSyncRef.current = true;
          form?.setValue('duration', snappedDuration);
          form?.setValue('dateTime', `${startIso}|${endIso}`);
          skipSyncRef.current = false;
          break;
        }

        case 'duration': {
          const newDuration = value as number;
          if (!sharedState.bookedSlot?.start) {
            // No slot yet — still remember the duration so the calendar
            // overlay knows what length of slots to offer.
            updateSharedState({
              duration: newDuration,
              bookedSlot: sharedState.bookedSlot
                ? { ...sharedState.bookedSlot, duration: newDuration }
                : null,
            });
            break;
          }
          const newEnd = addMinutesToIso(
            sharedState.bookedSlot.start,
            newDuration
          );
          const endTimeStr = formatTime12(newEnd, timezone);
          updateSharedState({
            duration: newDuration,
            bookedSlot: {
              ...sharedState.bookedSlot,
              duration: newDuration,
              end: newEnd,
            },
          });
          skipSyncRef.current = true;
          form?.setValue('endTime', findClosestTimeOption(endTimeStr));
          form?.setValue(
            'dateTime',
            `${sharedState.bookedSlot.start}|${newEnd}`
          );
          skipSyncRef.current = false;
          break;
        }

        // --- Other field syncs ---
        case 'participant_ids': {
          const ids = Array.isArray(value)
            ? (value as string[]).map(Number)
            : value
              ? [Number(value)]
              : [];
          updateSharedState({ interviewerIds: ids });
          break;
        }
        case 'interview_type': {
          // Keep the calendar's union/intersection mode in sync with
          // the form — switching panel ↔ one-on-one mid-edit otherwise
          // leaves sharedState.interviewKind stale and the availability
          // algorithm picks the wrong strategy.
          if (value === 'panel' || value === 'one_on_one') {
            updateSharedState({ interviewKind: value });
          }
          break;
        }
        case 'meeting_room_ids': {
          // The field can be single (string email) or multi (string[]) —
          // normalise to string[] for downstream consumers. Empty or
          // non-string values drop so the room-busy fetch stays clean.
          const raw = Array.isArray(value) ? value : [value];
          const emails = raw.filter(
            (v): v is string => typeof v === 'string' && v.length > 0
          );
          updateSharedState({ meetingRoomEmails: emails });
          break;
        }
        case 'location':
          updateSharedState({ location: value as string });
          break;
        case 'host_id':
          updateSharedState({ hostId: value ? Number(value) : null });
          break;
        case 'calendar_visibility':
          updateSharedState({
            calendarVisibility: value ? 'private' : 'default',
          });
          break;
        case 'enable_note_taker':
          updateSharedState({ noteTakerEnabled: Boolean(value) });
          break;
      }
    },
    [formRef, sharedState.bookedSlot, updateSharedState, calendarRef, timezone]
  );

  // ---- Calendar → Form: when bookedSlot changes, update all form fields ----
  useEffect(() => {
    const form = formRef.current;
    if (!form || !sharedState.bookedSlot) return;

    skipSyncRef.current = true;
    try {
      const { start, end, duration } = sharedState.bookedSlot;
      if (start && end) {
        const startTimeStr = findClosestTimeOption(
          formatTime12(start, timezone)
        );
        const endTimeStr = findClosestTimeOption(formatTime12(end, timezone));
        form.setValue('dateTime', `${start}|${end}`);
        form.setValue('startDate', new Date(start));
        form.setValue('startTime', startTimeStr);
        form.setValue('endTime', endTimeStr);
      }
      if (duration) {
        form.setValue('duration', duration);
      }
    } finally {
      skipSyncRef.current = false;
    }
  }, [formRef, sharedState.bookedSlot, timezone]);

  return { handleFieldChange };
}
